import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    getCourse, createCourse, updateCourse, deleteCourse, publishCourse,
    createModule, updateModule, deleteModule,
    createLesson, updateLesson, deleteLesson,
    uploadVideo, deleteVideo, reorderLessons,
    uploadFile, deleteFile,
    createTest, updateTest, addQuestion, updateQuestion, deleteQuestion,
    importTestQuestions,
} from '../../services/courses';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import VideoPlayer from '../../components/video/VideoPlayer';
import { ConfirmDialog, PromptDialog, useToast } from '../../components/ui/Modal';
import {
    Link2, Check, X, Video, Trash2, Clock, FileText, Save, Pencil,
    ArrowLeft, Rocket, ClipboardList, BookOpen, Circle, Globe,
    Plus, GripVertical, ChevronRight, Package, FileVideo, ChevronDown,
    CircleDot, ToggleLeft, MessageSquare, HelpCircle, CheckCircle, XCircle,
    Upload, Download, FileUp,
} from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/* ─── Tiny Rich-Text Toolbar ─── */
function RichEditor({ value, onChange }) {
    const editorRef = useRef(null);
    const isInternalChange = useRef(false);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const linkInputRef = useRef(null);

    useEffect(() => {
        if (editorRef.current && !isInternalChange.current) {
            if (editorRef.current.innerHTML !== (value || '')) {
                editorRef.current.innerHTML = value || '';
            }
        }
        isInternalChange.current = false;
    }, [value]);

    useEffect(() => {
        if (showLinkInput) {
            setTimeout(() => linkInputRef.current?.focus(), 50);
        }
    }, [showLinkInput]);

    const exec = (cmd, val = null) => {
        document.execCommand(cmd, false, val);
        editorRef.current?.focus();
    };

    const handleInput = () => {
        isInternalChange.current = true;
        onChange(editorRef.current.innerHTML);
    };

    const handleInsertLink = () => {
        if (linkUrl.trim()) {
            exec('createLink', linkUrl.trim());
        }
        setLinkUrl('');
        setShowLinkInput(false);
    };

    const btnClass = "px-2 py-1 text-sm rounded hover:bg-gray-200 transition font-medium";

    return (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border-b border-gray-200">
                <button type="button" onClick={() => exec('bold')} className={btnClass}><b>B</b></button>
                <button type="button" onClick={() => exec('italic')} className={btnClass}><i>I</i></button>
                <button type="button" onClick={() => exec('underline')} className={btnClass}><u>U</u></button>
                <span className="w-px bg-gray-300 mx-1" />
                <button type="button" onClick={() => exec('formatBlock', '<h2>')} className={btnClass}>H2</button>
                <button type="button" onClick={() => exec('formatBlock', '<h3>')} className={btnClass}>H3</button>
                <button type="button" onClick={() => exec('formatBlock', '<p>')} className={btnClass}>P</button>
                <span className="w-px bg-gray-300 mx-1" />
                <button type="button" onClick={() => exec('insertUnorderedList')} className={btnClass}>• List</button>
                <button type="button" onClick={() => exec('insertOrderedList')} className={btnClass}>1. List</button>
                <span className="w-px bg-gray-300 mx-1" />
                <div className="relative">
                    <button type="button" onClick={() => setShowLinkInput(!showLinkInput)} className={btnClass}><Link2 className="w-3.5 h-3.5" /></button>
                    {showLinkInput && (
                        <div className="absolute top-full left-0 mt-1 z-10 flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-lg p-1.5">
                            <input
                                ref={linkInputRef}
                                value={linkUrl}
                                onChange={e => setLinkUrl(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleInsertLink(); } if (e.key === 'Escape') setShowLinkInput(false); }}
                                placeholder="https://..."
                                className="px-2 py-1 border border-gray-200 rounded text-xs outline-none focus:border-blue-500 w-48"
                            />
                            <button type="button" onClick={handleInsertLink} className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"><Check className="w-3 h-3" /></button>
                            <button type="button" onClick={() => setShowLinkInput(false)} className="px-1.5 py-1 text-gray-400 hover:text-gray-600 text-xs"><X className="w-3 h-3" /></button>
                        </div>
                    )}
                </div>
                <button type="button" onClick={() => exec('removeFormat')} className={btnClass}><X className="w-3.5 h-3.5" /></button>
            </div>
            <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                className="p-4 min-h-[200px] outline-none prose prose-sm dark:prose-invert max-w-none"
                suppressContentEditableWarning
            />
        </div>
    );
}

/* ─── Video Uploader ─── */
function VideoUploader({ lessonId, existingVideo, onUploaded }) {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const fileRef = useRef(null);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const maxSize = 512 * 1024 * 1024;
        if (file.size > maxSize) {
            setError('Video hajmi 512MB dan oshmasligi kerak');
            return;
        }

        setUploading(true);
        setProgress(0);
        setError('');

        const formData = new FormData();
        formData.append('video', file);

        try {
            const res = await uploadVideo(lessonId, formData, (e) => {
                if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
            });
            onUploaded(res.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Video yuklashda xatolik');
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const handleDelete = async () => {
        try {
            await deleteVideo(lessonId);
            onUploaded(null);
        } catch {
            setError("O'chirishda xatolik");
        }
    };

    return (
        <div className="space-y-3">
            <ConfirmDialog
                open={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleDelete}
                title="Videoni o'chirish"
                message="Bu video butunlay o'chiriladi. Davom etasizmi?"
                icon={<Video className="w-7 h-7 text-red-500" />}
                confirmText="Ha, o'chirish"
                danger
            />

            {existingVideo && (
                <div className="relative">
                    <VideoPlayer src={existingVideo} />
                    <button onClick={() => setShowDeleteConfirm(true)} className="absolute top-2 right-2 bg-red-500 text-white rounded-lg px-3 py-1 text-xs font-medium hover:bg-red-600 flex items-center gap-1">
                        <Trash2 className="w-3 h-3" /> O'chirish
                    </button>
                </div>
            )}

            <div className="flex items-center gap-3">
                <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium cursor-pointer transition
                    ${uploading ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}>
                    {uploading ? (
                        <span className="flex items-center gap-1"><Clock className="w-4 h-4 animate-spin" /> Yuklanmoqda... {progress}%</span>
                    ) : (
                        <span className="flex items-center gap-1"><Video className="w-4 h-4" /> {existingVideo ? 'Almashtrish' : 'Video yuklash'}</span>
                    )}
                    <input
                        ref={fileRef}
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm,video/x-matroska,.mp4,.mov,.webm,.mkv"
                        onChange={handleUpload}
                        disabled={uploading}
                        className="hidden"
                    />
                </label>
                <span className="text-xs text-gray-400">MP4, MOV, WebM, MKV — max 512MB</span>
            </div>

            {uploading && (
                <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
            )}
            {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
    );
}

/* ─── File Uploader (PDF / Presentations) — Multi-file ─── */
function FileUploader({ lessonId, existingFiles, onUploaded }) {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const fileRef = useRef(null);

    const getFileIcon = (name) => {
        const ext = (name || '').split('.').pop().toLowerCase();
        if (ext === 'pdf') return '📄';
        if (['ppt', 'pptx'].includes(ext)) return '📊';
        if (['doc', 'docx'].includes(ext)) return '📝';
        if (['xls', 'xlsx'].includes(ext)) return '📈';
        if (ext === 'zip') return '📦';
        return '📎';
    };

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const maxSize = 50 * 1024 * 1024;
        if (file.size > maxSize) {
            setError('Fayl hajmi 50MB dan oshmasligi kerak');
            return;
        }

        setUploading(true);
        setProgress(0);
        setError('');

        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await uploadFile(lessonId, formData, (e) => {
                if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
            });
            onUploaded(res.data.files);
        } catch (err) {
            setError(err.response?.data?.message || 'Fayl yuklashda xatolik');
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            const res = await deleteFile(lessonId, deleteTarget);
            onUploaded(res.data.files);
        } catch {
            setError("O'chirishda xatolik");
        } finally {
            setDeleteTarget(null);
        }
    };

    const files = existingFiles || [];

    return (
        <div className="space-y-3">
            <ConfirmDialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Faylni o'chirish"
                message="Bu fayl butunlay o'chiriladi. Davom etasizmi?"
                icon={<FileUp className="w-7 h-7 text-red-500" />}
                confirmText="Ha, o'chirish"
                danger
            />

            {files.length > 0 && (
                <div className="space-y-2">
                    {files.map((f, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
                            <span className="text-2xl shrink-0">{getFileIcon(f.original_name)}</span>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-700 truncate">{f.original_name}</p>
                                <a href={`/storage/${f.path}`} target="_blank" rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline">Ko'rish / Yuklab olish</a>
                            </div>
                            <button onClick={() => setDeleteTarget(f.path)}
                                className="bg-red-500 text-white rounded-lg px-3 py-1 text-xs font-medium hover:bg-red-600 flex items-center gap-1 shrink-0">
                                <Trash2 className="w-3 h-3" /> O'chirish
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex items-center gap-3">
                <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium cursor-pointer transition
                    ${uploading ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}>
                    {uploading ? (
                        <span className="flex items-center gap-1"><Clock className="w-4 h-4 animate-spin" /> Yuklanmoqda... {progress}%</span>
                    ) : (
                        <span className="flex items-center gap-1"><FileUp className="w-4 h-4" /> Fayl qo'shish</span>
                    )}
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.txt,.zip"
                        onChange={handleUpload}
                        disabled={uploading}
                        className="hidden"
                    />
                </label>
                <span className="text-xs text-gray-400">PDF, PPT, DOC, XLS, TXT, ZIP — max 50MB</span>
            </div>

            {uploading && (
                <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
            )}
            {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
    );
}

/* ─── Test Editor (Teacher) ─── */
function TestEditor({ lesson, onTestChange }) {
    const toast = useToast();
    const [test, setTest] = useState(lesson.test || null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const importInputRef = useRef(null);

    // Settings form
    const [settings, setSettings] = useState({
        title: test?.title || lesson.title + ' - Test',
        passing_score: test?.passing_score || 60,
        time_limit_minutes: test?.time_limit_minutes || '',
        max_attempts: test?.max_attempts || 3,
        shuffle_questions: test?.shuffle_questions || false,
        show_results_immediately: test?.show_results_immediately ?? true,
    });

    // Question editing
    const [editingQuestion, setEditingQuestion] = useState(null); // null = list, 'new' = add, {question} = edit
    const [qForm, setQForm] = useState({ text: '', type: 'multiple_choice', points: 1, explanation: '', answers: [{ text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }] });

    const questionTypes = [
        { val: 'multiple_choice', icon: CircleDot, label: "Ko'p tanlovli" },
        { val: 'true_false', icon: ToggleLeft, label: "To'g'ri / Noto'g'ri" },
        { val: 'open', icon: MessageSquare, label: 'Ochiq javob' },
    ];

    // Create test if not exists
    const handleCreateTest = async () => {
        setLoading(true);
        try {
            const r = await createTest(lesson.id, settings);
            setTest(r.data);
            onTestChange?.(r.data);
            toast.success('Test yaratildi');
        } catch (e) {
            toast.error(e.response?.data?.message || 'Test yaratishda xatolik');
        } finally {
            setLoading(false);
        }
    };

    // Save test settings
    const handleSaveSettings = async () => {
        if (!test) return;
        setSaving(true);
        try {
            const r = await updateTest(test.id, settings);
            setTest(prev => ({ ...prev, ...r.data }));
            toast.success('Test sozlamalari saqlandi');
        } catch {
            toast.error('Saqlashda xatolik');
        } finally {
            setSaving(false);
        }
    };

    // Start adding a question
    const startAddQuestion = (type = 'multiple_choice') => {
        const defaultAnswers = type === 'true_false'
            ? [{ text: "To'g'ri", is_correct: true }, { text: "Noto'g'ri", is_correct: false }]
            : type === 'open'
                ? []
                : [{ text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }];
        setQForm({ text: '', type, points: 1, explanation: '', answers: defaultAnswers });
        setEditingQuestion('new');
    };

    // Start editing existing question
    const startEditQuestion = (question) => {
        setQForm({
            text: question.text,
            type: question.type,
            points: question.points || 1,
            explanation: question.explanation || '',
            answers: question.answers?.map(a => ({ text: a.text, is_correct: a.is_correct })) || [],
        });
        setEditingQuestion(question);
    };

    // Change question type in form
    const handleTypeChange = (type) => {
        const defaultAnswers = type === 'true_false'
            ? [{ text: "To'g'ri", is_correct: true }, { text: "Noto'g'ri", is_correct: false }]
            : type === 'open'
                ? []
                : [{ text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }];
        setQForm(prev => ({ ...prev, type, answers: defaultAnswers }));
    };

    // Save question (add or update)
    const handleSaveQuestion = async () => {
        if (!qForm.text.trim()) {
            toast.error('Savol matnini kiriting');
            return;
        }
        if (qForm.type === 'multiple_choice' && !qForm.answers.some(a => a.is_correct)) {
            toast.error("Kamida bitta to'g'ri javobni belgilang");
            return;
        }
        setSaving(true);
        try {
            if (editingQuestion === 'new') {
                const r = await addQuestion(test.id, qForm);
                setTest(prev => ({ ...prev, questions: [...(prev.questions || []), r.data] }));
                toast.success('Savol qo\'shildi');
            } else {
                const r = await updateQuestion(editingQuestion.id, qForm);
                setTest(prev => ({
                    ...prev,
                    questions: prev.questions.map(q => q.id === editingQuestion.id ? r.data : q),
                }));
                toast.success('Savol yangilandi');
            }
            setEditingQuestion(null);
        } catch (e) {
            toast.error(e.response?.data?.message || 'Saqlashda xatolik');
        } finally {
            setSaving(false);
        }
    };

    // Delete question
    const handleDeleteQuestion = async (questionId) => {
        try {
            await deleteQuestion(questionId);
            setTest(prev => ({
                ...prev,
                questions: prev.questions.filter(q => q.id !== questionId),
            }));
            toast.success("Savol o'chirildi");
        } catch {
            toast.error("O'chirishda xatolik");
        }
    };

    // Import from Excel
    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        setImporting(true);
        try {
            const r = await importTestQuestions(test.id, file);
            setTest(r.data.test);
            onTestChange?.(r.data.test);
            toast.success(r.data.message);
            if (r.data.errors?.length > 0) {
                toast.error(`Xatoliklar: ${r.data.errors.join('; ')}`);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Import xatoligi');
        } finally {
            setImporting(false);
        }
    };

    const handleDownloadTemplate = () => {
        const url = `${api.defaults.baseURL}/tests/import-template`;
        const token = localStorage.getItem('token');
        // Use fetch with auth header to download
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.blob())
            .then(blob => {
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = 'test_import_shablon.xlsx';
                a.click();
                URL.revokeObjectURL(a.href);
            })
            .catch(() => toast.error('Shablonni yuklab olishda xatolik'));
    };

    // Set answer correctness for multiple choice
    const toggleCorrect = (index) => {
        setQForm(prev => ({
            ...prev,
            answers: prev.answers.map((a, i) => ({ ...a, is_correct: i === index })),
        }));
    };

    // No test yet — create
    if (!test) {
        return (
            <div className="space-y-4">
                <div className="p-6 bg-gray-50 rounded-xl border border-dashed border-gray-300 text-center">
                    <HelpCircle className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <h4 className="font-semibold text-gray-900 mb-1">Bu dars uchun test yaratish</h4>
                    <p className="text-sm text-gray-500 mb-4">Test yaratib, unga savollar qo'shing</p>
                    <button onClick={handleCreateTest} disabled={loading}
                        className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                        {loading ? 'Yaratilmoqda...' : 'Test yaratish'}
                    </button>
                </div>
            </div>
        );
    }

    // Question form (add/edit)
    if (editingQuestion) {
        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">
                        {editingQuestion === 'new' ? 'Yangi savol' : 'Savolni tahrirlash'}
                    </h4>
                    <button onClick={() => setEditingQuestion(null)} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Question type selector */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Savol turi</label>
                    <div className="flex gap-2">
                        {questionTypes.map(qt => (
                            <button key={qt.val} onClick={() => handleTypeChange(qt.val)}
                                className={`px-3 py-2 rounded-xl text-sm font-medium border flex items-center gap-1.5 transition
                                    ${qForm.type === qt.val ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                                <qt.icon className="w-4 h-4" /> {qt.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Question text */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Savol matni *</label>
                    <textarea value={qForm.text} onChange={e => setQForm(p => ({ ...p, text: e.target.value }))}
                        rows={3} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm resize-none"
                        placeholder="Savolni kiriting..." />
                </div>

                {/* Points */}
                <div className="w-32">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ball</label>
                    <input type="number" min="1" max="100" value={qForm.points}
                        onChange={e => setQForm(p => ({ ...p, points: parseInt(e.target.value) || 1 }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm" />
                </div>

                {/* Answers — multiple choice */}
                {qForm.type === 'multiple_choice' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Javob variantlari</label>
                        <div className="space-y-2">
                            {qForm.answers.map((ans, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <button onClick={() => toggleCorrect(i)} title={ans.is_correct ? "To'g'ri javob" : "To'g'ri deb belgilash"}
                                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition
                                            ${ans.is_correct ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300 hover:border-green-400'}`}>
                                        {ans.is_correct && <Check className="w-3.5 h-3.5" />}
                                    </button>
                                    <input value={ans.text}
                                        onChange={e => setQForm(p => ({
                                            ...p,
                                            answers: p.answers.map((a, j) => j === i ? { ...a, text: e.target.value } : a),
                                        }))}
                                        className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-blue-500"
                                        placeholder={`Variant ${String.fromCharCode(65 + i)}`} />
                                    {qForm.answers.length > 2 && (
                                        <button onClick={() => setQForm(p => ({ ...p, answers: p.answers.filter((_, j) => j !== i) }))}
                                            className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                                    )}
                                </div>
                            ))}
                            {qForm.answers.length < 6 && (
                                <button onClick={() => setQForm(p => ({ ...p, answers: [...p.answers, { text: '', is_correct: false }] }))}
                                    className="text-sm text-blue-600 hover:text-blue-700 font-medium mt-1">+ Variant qo'shish</button>
                            )}
                        </div>
                        <p className="text-xs text-gray-400 mt-2">Yashil doirachani bosib to'g'ri javobni belgilang</p>
                    </div>
                )}

                {/* Answers — true/false */}
                {qForm.type === 'true_false' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">To'g'ri javob</label>
                        <div className="flex gap-3">
                            {qForm.answers.map((ans, i) => (
                                <button key={i} onClick={() => setQForm(p => ({
                                    ...p,
                                    answers: p.answers.map((a, j) => ({ ...a, is_correct: j === i })),
                                }))}
                                    className={`flex-1 px-4 py-3 rounded-xl border-2 font-medium text-sm transition flex items-center justify-center gap-2
                                        ${ans.is_correct ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                                    {ans.is_correct ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                                    {ans.text}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Open answer notice */}
                {qForm.type === 'open' && (
                    <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                        <p className="text-sm text-amber-700">Ochiq javobli savollar o'qituvchi tomonidan tekshiriladi. Avtomatik baholanmaydi.</p>
                    </div>
                )}

                {/* Explanation */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tushuntirish (ixtiyoriy)</label>
                    <textarea value={qForm.explanation} onChange={e => setQForm(p => ({ ...p, explanation: e.target.value }))}
                        rows={2} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm resize-none"
                        placeholder="Javobdan keyin ko'rsatiladigan tushuntirish..." />
                </div>

                {/* Save/Cancel */}
                <div className="flex gap-2 pt-2">
                    <button onClick={() => setEditingQuestion(null)} className="px-4 py-2 text-sm text-gray-600 rounded-xl border border-gray-200 hover:bg-gray-50">Bekor</button>
                    <button onClick={handleSaveQuestion} disabled={saving}
                        className="px-6 py-2 text-sm bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
                        {saving ? 'Saqlanmoqda...' : <><Save className="w-4 h-4" /> Saqlash</>}
                    </button>
                </div>
            </div>
        );
    }

    // Main test view — settings + question list
    return (
        <div className="space-y-5">
            {/* Test settings */}
            <details className="group">
                <summary className="cursor-pointer text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3 select-none">
                    <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                    Test sozlamalari
                </summary>
                <div className="grid grid-cols-2 gap-3 pl-6">
                    <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Test nomi</label>
                        <input value={settings.title} onChange={e => setSettings(s => ({ ...s, title: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">O'tish balli (%)</label>
                        <input type="number" min="0" max="100" value={settings.passing_score}
                            onChange={e => setSettings(s => ({ ...s, passing_score: parseInt(e.target.value) || 0 }))}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Vaqt limiti (daqiqa)</label>
                        <input type="number" min="0" value={settings.time_limit_minutes}
                            onChange={e => setSettings(s => ({ ...s, time_limit_minutes: e.target.value ? parseInt(e.target.value) : '' }))}
                            placeholder="Cheksiz" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Maks. urinishlar</label>
                        <input type="number" min="1" max="100" value={settings.max_attempts}
                            onChange={e => setSettings(s => ({ ...s, max_attempts: parseInt(e.target.value) || 1 }))}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500" />
                    </div>
                    <div className="flex items-center gap-2 pt-5">
                        <input type="checkbox" id="shuffle" checked={settings.shuffle_questions}
                            onChange={e => setSettings(s => ({ ...s, shuffle_questions: e.target.checked }))}
                            className="rounded border-gray-300" />
                        <label htmlFor="shuffle" className="text-xs text-gray-700">Aralash tartib</label>
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                        <input type="checkbox" id="showResults" checked={settings.show_results_immediately}
                            onChange={e => setSettings(s => ({ ...s, show_results_immediately: e.target.checked }))}
                            className="rounded border-gray-300" />
                        <label htmlFor="showResults" className="text-xs text-gray-700">Natijalarni darhol ko'rsatish</label>
                    </div>
                    <div className="col-span-2">
                        <button onClick={handleSaveSettings} disabled={saving}
                            className="px-4 py-1.5 text-xs bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                            {saving ? '...' : 'Sozlamalarni saqlash'}
                        </button>
                    </div>
                </div>
            </details>

            {/* Questions list */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700">Savollar ({test.questions?.length || 0})</h4>
                    <div className="flex gap-1">
                        {questionTypes.map(qt => (
                            <button key={qt.val} onClick={() => startAddQuestion(qt.val)} title={qt.label}
                                className="p-1.5 rounded-lg text-gray-500 hover:bg-blue-50 hover:text-blue-600 transition">
                                <qt.icon className="w-4 h-4" />
                            </button>
                        ))}
                    </div>
                </div>

                {test.questions?.length > 0 ? (
                    <div className="space-y-2">
                        {test.questions.map((q, idx) => (
                            <div key={q.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl group hover:bg-blue-50 transition">
                                <span className="text-xs font-bold text-gray-400 mt-0.5 w-5 text-center">{idx + 1}</span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-900 leading-snug line-clamp-2">{q.text}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium
                                            ${q.type === 'multiple_choice' ? 'bg-purple-100 text-purple-700' :
                                              q.type === 'true_false' ? 'bg-cyan-100 text-cyan-700' :
                                              'bg-amber-100 text-amber-700'}`}>
                                            {q.type === 'multiple_choice' ? "Ko'p tanlovli" : q.type === 'true_false' ? "To'g'ri/Noto'g'ri" : 'Ochiq'}
                                        </span>
                                        <span className="text-xs text-gray-400">{q.points} ball</span>
                                    </div>
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                                    <button onClick={() => startEditQuestion(q)} className="p-1 hover:bg-white rounded text-gray-500 hover:text-blue-600">
                                        <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={() => handleDeleteQuestion(q.id)} className="p-1 hover:bg-white rounded text-gray-500 hover:text-red-600">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-6 text-gray-400 text-sm">
                        <HelpCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Hali savollar yo'q. Yuqoridagi tugmalar orqali qo'shing.</p>
                    </div>
                )}

                {/* Add question button */}
                <button onClick={() => startAddQuestion('multiple_choice')}
                    className="w-full mt-3 py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-blue-500 hover:text-blue-600 font-medium transition flex items-center justify-center gap-1.5">
                    <Plus className="w-4 h-4" /> Savol qo'shish
                </button>

                {/* Import from Excel & Download template */}
                <div className="flex gap-2 mt-2">
                    <input ref={importInputRef} type="file" accept=".xlsx,.xls" onChange={handleImportFile} className="hidden" />
                    <button onClick={() => importInputRef.current?.click()} disabled={importing}
                        className="flex-1 py-2 border border-green-200 rounded-xl text-sm text-green-600 hover:bg-green-50 font-medium transition flex items-center justify-center gap-1.5 disabled:opacity-50">
                        {importing ? 'Import...' : <><Upload className="w-4 h-4" /> Exceldan import</>}
                    </button>
                    <button onClick={handleDownloadTemplate}
                        className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-gray-500 hover:bg-gray-50 font-medium transition flex items-center justify-center gap-1.5">
                        <Download className="w-4 h-4" /> Shablon yuklab olish
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Lesson Editor Panel ─── */
function LessonEditor({ lesson, onSave, onDelete, onClose }) {
    const [title, setTitle] = useState(lesson.title);
    const [type, setType] = useState(lesson.test ? 'test' : lesson.type || 'text');
    const [htmlContent, setHtmlContent] = useState('');
    const [videoUrl, setVideoUrl] = useState(null);
    const [filesData, setFilesData] = useState([]);
    const [saving, setSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const toast = useToast();

    useEffect(() => {
        const contents = lesson.contents || [];
        const html = contents.find(c => c.content_type === 'html');
        const video = contents.find(c => c.content_type === 'hls_video');
        const file = contents.find(c => c.content_type === 'file_path');

        if (html) {
            setHtmlContent(html.content_data || '');
        }
        if (video) {
            try {
                const data = JSON.parse(video.content_data);
                setVideoUrl(`/storage/${data.playlist}`);
            } catch { /* ignore */ }
        }
        if (file) {
            try {
                const data = JSON.parse(file.content_data);
                // New format: array of files; old format: single {path, original_name}
                if (Array.isArray(data)) {
                    setFilesData(data);
                } else if (data.path) {
                    setFilesData([data]);
                }
            } catch {
                setFilesData([{ path: file.content_data, original_name: file.content_data.split('/').pop() }]);
            }
        }
    }, [lesson]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const data = { title, type };
            if (type !== 'test' && type !== 'file' && htmlContent) {
                data.content_type = 'html';
                data.content_data = htmlContent;
            }
            await onSave(lesson.id, data);
            toast.success('Dars muvaffaqiyatli saqlandi');
        } catch {
            toast.error('Saqlashda xatolik yuz berdi');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900">Darsni tahrirlash</h3>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-5">
                {/* Title */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dars nomi</label>
                    <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm" />
                </div>

                {/* Type */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dars turi</label>
                    <div className="flex gap-2 flex-wrap">
                        {[
                            { val: 'text', icon: FileText, label: 'Matn' },
                            { val: 'video', icon: Video, label: 'Video' },
                            { val: 'text_video', icon: null, label: 'Matn + Video', combined: true },
                            { val: 'file', icon: FileUp, label: 'Fayl/PDF' },
                            { val: 'test', icon: ClipboardList, label: 'Test' },
                        ].map(t => (
                            <button
                                key={t.label}
                                type="button"
                                onClick={() => setType(t.combined ? 'video' : t.val)}
                                className={`px-4 py-2 rounded-xl text-sm font-medium border transition flex items-center gap-1.5
                                    ${(t.combined ? type === 'video' : type === t.val && !t.combined) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                            >
                                {t.combined ? <><FileText className="w-4 h-4" /><Video className="w-4 h-4" /></> : t.icon && <t.icon className="w-4 h-4" />} {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Video Upload */}
                {(type === 'video') && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Video</label>
                        <VideoUploader
                            lessonId={lesson.id}
                            existingVideo={videoUrl}
                            onUploaded={(result) => {
                                if (result) {
                                    setVideoUrl(result.playlist_url);
                                } else {
                                    setVideoUrl(null);
                                }
                            }}
                        />
                    </div>
                )}

                {/* File Upload (PDF, Presentations, etc.) */}
                {type === 'file' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Fayllar yuklash</label>
                        <FileUploader
                            lessonId={lesson.id}
                            existingFiles={filesData}
                            onUploaded={(files) => setFilesData(files || [])}
                        />
                    </div>
                )}

                {/* Rich Text Editor — always shown for text+video combo or pure text */}
                {type !== 'test' && type !== 'file' && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dars matni (HTML)</label>
                    <RichEditor value={htmlContent} onChange={setHtmlContent} />
                </div>
                )}

                {/* Test Editor */}
                {type === 'test' && (
                    <TestEditor lesson={lesson} onTestChange={(t) => {
                        // Update lesson.test locally for re-renders
                        lesson.test = t;
                    }} />
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <button onClick={() => setShowDeleteConfirm(true)} className="text-sm text-red-500 hover:text-red-700 font-medium flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> O'chirish</button>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 rounded-xl border border-gray-200 hover:bg-gray-50">Bekor</button>
                        <button onClick={handleSave} disabled={saving} className="px-6 py-2 text-sm bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
                            {saving ? 'Saqlanmoqda...' : <><Save className="w-4 h-4" /> Saqlash</>}
                        </button>
                    </div>
                </div>

                <ConfirmDialog
                    open={showDeleteConfirm}
                    onClose={() => setShowDeleteConfirm(false)}
                    onConfirm={() => onDelete(lesson.id)}
                    title="Darsni o'chirish"
                    message={`"${lesson.title}" darsi butunlay o'chiriladi. Davom etasizmi?`}
                    icon={<FileText className="w-7 h-7 text-red-500" />}
                    confirmText="Ha, o'chirish"
                    danger
                />
            </div>
        </div>
    );
}

/* ─── Sortable Lesson Item ─── */
function SortableLessonItem({ lesson, isActive, moduleIndex, lessonIndex, onClick }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: lesson.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : 'auto',
    };
    const hasVideo = lesson.contents?.some(c => c.content_type === 'hls_video');
    const hasText = lesson.contents?.some(c => c.content_type === 'html' && c.content_data);
    const hasTest = lesson.test || lesson.type === 'test';
    const hasFile = lesson.contents?.some(c => c.content_type === 'file_path');

    return (
        <div ref={setNodeRef} style={style} className={`flex items-center gap-1 text-sm transition border-b border-gray-50 last:border-0
            ${isActive ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700'}`}>
            <button {...attributes} {...listeners} className="px-1.5 py-2.5 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500">
                <GripVertical className="w-3.5 h-3.5" />
            </button>
            <button onClick={onClick} className="flex-1 text-left py-2.5 pr-4 flex items-center gap-2">
                <span className="text-gray-400 text-xs w-5">{moduleIndex + 1}.{lessonIndex + 1}</span>
                <span className="flex-1 truncate">{lesson.title}</span>
                <span className="flex gap-1 text-xs">
                    {hasVideo && <span title="Video"><FileVideo className="w-3.5 h-3.5 text-purple-500" /></span>}
                    {hasText && <span title="Matn"><FileText className="w-3.5 h-3.5 text-blue-500" /></span>}
                    {hasFile && <span title="Fayl"><FileUp className="w-3.5 h-3.5 text-orange-500" /></span>}
                    {hasTest && <span title="Test"><ClipboardList className="w-3.5 h-3.5 text-green-500" /></span>}
                </span>
            </button>
        </div>
    );
}

/* ──────────────────────────── MAIN: Course Editor ──────────────────────────── */
export default function CourseEditor() {
    const { id } = useParams(); // undefined for new course
    const navigate = useNavigate();
    const { user } = useAuth();
    const toast = useToast();

    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(!!id);
    const [saving, setSaving] = useState(false);
    const [activeLesson, setActiveLesson] = useState(null);
    const [tab, setTab] = useState('info');

    // Dialog states
    const [modulePrompt, setModulePrompt] = useState({ open: false, moduleId: null, defaultValue: '' });
    const [lessonPrompt, setLessonPrompt] = useState({ open: false, moduleId: null });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', icon: null, onConfirm: null, danger: false });

    // Form state
    const [form, setForm] = useState({
        title: '', description: '', category_id: '', difficulty_level: 'beginner',
        language: 'uz', requirements: '', objectives: [''], estimated_hours: '',
        tags: [''],
    });

    // Categories
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        api.get('/categories').then(r => setCategories(r.data));
    }, []);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        getCourse(id)
            .then(r => {
                const c = r.data;
                setCourse(c);
                setForm({
                    title: c.title || '',
                    description: c.description || '',
                    category_id: c.category_id || '',
                    difficulty_level: c.difficulty_level || 'beginner',
                    language: c.language || 'uz',
                    requirements: c.requirements || '',
                    objectives: c.objectives?.length ? c.objectives : [''],
                    estimated_hours: c.estimated_hours || '',
                    tags: c.tags?.length ? c.tags : [''],
                });
            })
            .finally(() => setLoading(false));
    }, [id]);

    const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

    // ─── Course CRUD ───
    const handleSaveCourse = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                objectives: form.objectives.filter(o => o.trim()),
                tags: form.tags.filter(t => t.trim()),
                estimated_hours: form.estimated_hours ? Number(form.estimated_hours) : null,
                category_id: form.category_id || null,
            };
            if (course) {
                const r = await updateCourse(course.id, payload);
                setCourse(r.data);
                toast.success('Kurs muvaffaqiyatli yangilandi');
            } else {
                const r = await createCourse(payload);
                setCourse(r.data);
                toast.success('Kurs muvaffaqiyatli yaratildi!');
                navigate(`/teacher/courses/${r.data.id}/edit`, { replace: true });
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Saqlashda xatolik yuz berdi');
        } finally {
            setSaving(false);
        }
    };

    const handlePublish = async () => {
        if (!course) return;
        try {
            const r = await publishCourse(course.id);
            setCourse(r.data);
            toast.success('Kurs muvaffaqiyatli nashr qilindi!');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Nashr qilishda xatolik');
        }
    };

    const handleDeleteCourse = () => {
        if (!course) return;
        setConfirmDialog({
            open: true,
            title: "Kursni o'chirish",
            message: `"${course.title}" kursi barcha modullari va darslari bilan birga butunlay o'chiriladi. Bu amalni qaytarib bo'lmaydi!`,
            icon: null,
            danger: true,
            onConfirm: async () => {
                await deleteCourse(course.id);
                toast.success("Kurs o'chirildi");
                navigate('/teacher/courses');
            },
        });
    };

    // ─── Module CRUD ───
    const handleAddModule = () => {
        if (!course) return;
        setModulePrompt({ open: true, moduleId: null, defaultValue: '' });
    };

    const handleEditModule = (moduleId) => {
        const mod = course.modules.find(m => m.id === moduleId);
        setModulePrompt({ open: true, moduleId, defaultValue: mod?.title || '' });
    };

    const handleModulePromptSubmit = async (title) => {
        if (modulePrompt.moduleId) {
            // Edit existing
            await updateModule(modulePrompt.moduleId, { title });
            setCourse(prev => ({
                ...prev,
                modules: prev.modules.map(m => m.id === modulePrompt.moduleId ? { ...m, title } : m),
            }));
            toast.success('Modul nomi yangilandi');
        } else {
            // Create new
            const r = await createModule(course.id, { title });
            setCourse(prev => ({ ...prev, modules: [...(prev.modules || []), r.data] }));
            toast.success('Yangi modul qo\'shildi');
        }
    };

    const handleDeleteModule = (moduleId) => {
        const mod = course.modules.find(m => m.id === moduleId);
        setConfirmDialog({
            open: true,
            title: "Modulni o'chirish",
            message: `"${mod?.title}" moduli va undagi barcha darslar o'chiriladi. Davom etasizmi?`,
            icon: null,
            danger: true,
            onConfirm: async () => {
                await deleteModule(moduleId);
                setCourse(prev => ({
                    ...prev,
                    modules: prev.modules.filter(m => m.id !== moduleId),
                }));
                toast.success("Modul o'chirildi");
            },
        });
    };

    // ─── Lesson CRUD ───
    const handleAddLesson = (moduleId) => {
        setLessonPrompt({ open: true, moduleId });
    };

    const handleLessonPromptSubmit = async (title) => {
        const r = await createLesson(lessonPrompt.moduleId, { title, type: 'text' });
        setCourse(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === lessonPrompt.moduleId
                    ? { ...m, lessons: [...(m.lessons || []), r.data] }
                    : m
            ),
        }));
        setActiveLesson(r.data);
        toast.success('Yangi dars qo\'shildi');
    };

    const handleSaveLesson = async (lessonId, data) => {
        const r = await updateLesson(lessonId, data);
        const updated = r.data;
        setCourse(prev => ({
            ...prev,
            modules: prev.modules.map(m => ({
                ...m,
                lessons: m.lessons?.map(l => l.id === lessonId ? updated : l),
            })),
        }));
        setActiveLesson(updated);
    };

    const handleDeleteLesson = (lessonId) => {
        const lesson = course.modules.flatMap(m => m.lessons || []).find(l => l.id === lessonId);
        setConfirmDialog({
            open: true,
            title: "Darsni o'chirish",
            message: `"${lesson?.title || 'Dars'}" darsi butunlay o'chiriladi. Davom etasizmi?`,
            icon: null,
            danger: true,
            onConfirm: async () => {
                await deleteLesson(lessonId);
                setCourse(prev => ({
                    ...prev,
                    modules: prev.modules.map(m => ({
                        ...m,
                        lessons: m.lessons?.filter(l => l.id !== lessonId),
                    })),
                }));
                setActiveLesson(null);
                toast.success("Dars o'chirildi");
            },
        });
    };

    // ─── DnD Lesson Reorder ───
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = useCallback(async (event, moduleId) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setCourse(prev => {
            const newModules = prev.modules.map(m => {
                if (m.id !== moduleId) return m;
                const oldIndex = m.lessons.findIndex(l => l.id === active.id);
                const newIndex = m.lessons.findIndex(l => l.id === over.id);
                if (oldIndex === -1 || newIndex === -1) return m;
                const newLessons = arrayMove(m.lessons, oldIndex, newIndex).map((l, i) => ({ ...l, order_index: i }));

                // Send reorder to backend
                reorderLessons(newLessons.map((l, i) => ({ id: l.id, order_index: i }))).catch(() => {
                    toast.error('Tartibni saqlashda xatolik');
                });

                return { ...m, lessons: newLessons };
            });
            return { ...prev, modules: newModules };
        });
    }, [toast]);

    // ─── Render ───
    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
        </div>
    );

    const totalLessons = course?.modules?.reduce((s, m) => s + (m.lessons?.length || 0), 0) || 0;

    return (
    <>
        <div className="max-w-6xl mx-auto space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between">
                    <div>
                        <Link to="/teacher/courses" className="text-sm text-indigo-200 hover:text-white mb-1 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Kurslarim</Link>
                        <h1 className="text-2xl font-bold flex items-center gap-2">{course ? 'Kursni tahrirlash' : 'Yangi kurs yaratish'} <Pencil className="w-5 h-5" /></h1>
                        {course && (
                            <div className="flex items-center gap-4 mt-2 text-sm text-indigo-200">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${course.status === 'published' ? 'bg-green-500/20 text-green-200' : 'bg-yellow-500/20 text-yellow-200'}`}>
                                    {course.status === 'published' ? 'Nashr qilingan' : 'Qoralama'}
                                </span>
                                <span>{course.modules?.length || 0} modul</span>
                                <span>{totalLessons} dars</span>
                            </div>
                        )}
                    </div>
                    {course && (
                        <div className="flex gap-2">
                            {course.status !== 'published' && (
                                <button onClick={handlePublish} className="px-5 py-2 bg-green-500 text-white rounded-xl text-sm font-medium hover:bg-green-600 transition flex items-center gap-1.5">
                                    <Rocket className="w-4 h-4" /> Nashr qilish
                                </button>
                            )}
                            <button onClick={handleDeleteCourse} className="px-4 py-2 bg-white/10 text-white rounded-xl text-sm hover:bg-white/20 transition">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
                {[
                    { key: 'info', label: 'Kurs ma\'lumotlari' },
                    { key: 'content', label: 'Tarkib (Modullar & Darslar)', disabled: !course },
                ].map(t => (
                    <button
                        key={t.key}
                        disabled={t.disabled}
                        onClick={() => setTab(t.key)}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition
                            ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}
                            ${t.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Tab: Course Info */}
            {tab === 'info' && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* Title */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Kurs nomi *</label>
                            <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Masalan: Python dasturlash asoslari"
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm" />
                        </div>

                        {/* Description */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tavsif</label>
                            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={4} placeholder="Kurs haqida qisqacha..."
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm resize-none" />
                        </div>

                        {/* Category */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Kategoriya</label>
                            <select value={form.category_id} onChange={e => set('category_id', e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm bg-white">
                                <option value="">Tanlang...</option>
                                {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                            </select>
                        </div>

                        {/* Difficulty */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Qiyinlik darajasi</label>
                            <select value={form.difficulty_level} onChange={e => set('difficulty_level', e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm bg-white">
                                <option value="beginner">Boshlang'ich</option>
                                <option value="intermediate">O'rta</option>
                                <option value="advanced">Yuqori</option>
                            </select>
                        </div>

                        {/* Language */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Til</label>
                            <select value={form.language} onChange={e => set('language', e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm bg-white">
                                <option value="uz">O'zbek</option>
                                <option value="ru">Rus</option>
                                <option value="en">Ingliz</option>
                            </select>
                        </div>

                        {/* Estimated hours */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Taxminiy vaqt (soat)</label>
                            <input type="number" min="0" step="0.5" value={form.estimated_hours} onChange={e => set('estimated_hours', e.target.value)}
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm" />
                        </div>

                        {/* Requirements */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Talablar</label>
                            <textarea value={form.requirements} onChange={e => set('requirements', e.target.value)} rows={2} placeholder="Oldindan bilishi kerak bo'lgan bilimlar..."
                                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm resize-none" />
                        </div>

                        {/* Objectives */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Kurs maqsadlari</label>
                            {form.objectives.map((obj, i) => (
                                <div key={i} className="flex gap-2 mb-2">
                                    <input value={obj} onChange={e => {
                                        const arr = [...form.objectives];
                                        arr[i] = e.target.value;
                                        set('objectives', arr);
                                    }} placeholder={`Maqsad ${i + 1}`}
                                        className="flex-1 px-4 py-2 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm" />
                                    {form.objectives.length > 1 && (
                                        <button type="button" onClick={() => set('objectives', form.objectives.filter((_, j) => j !== i))}
                                            className="text-red-400 hover:text-red-600 px-2"><X className="w-4 h-4" /></button>
                                    )}
                                </div>
                            ))}
                            <button type="button" onClick={() => set('objectives', [...form.objectives, ''])}
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium">+ Maqsad qo'shish</button>
                        </div>

                        {/* Tags */}
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Teglar</label>
                            <div className="flex flex-wrap gap-2">
                                {form.tags.map((tag, i) => (
                                    <div key={i} className="flex items-center gap-1">
                                        <input value={tag} onChange={e => {
                                            const arr = [...form.tags];
                                            arr[i] = e.target.value;
                                            set('tags', arr);
                                        }} placeholder="teg"
                                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-500 w-32" />
                                        {form.tags.length > 1 && (
                                            <button type="button" onClick={() => set('tags', form.tags.filter((_, j) => j !== i))}
                                                className="text-red-400 hover:text-red-600 text-xs"><X className="w-3.5 h-3.5" /></button>
                                        )}
                                    </div>
                                ))}
                                <button type="button" onClick={() => set('tags', [...form.tags, ''])}
                                    className="px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-500 hover:text-blue-600">+ Teg</button>
                            </div>
                        </div>
                    </div>

                    {/* Save */}
                    <div className="flex justify-end pt-4 border-t border-gray-100">
                        <button onClick={handleSaveCourse} disabled={saving || !form.title.trim()}
                            className="px-8 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 disabled:opacity-50 transition">
                            {saving ? 'Saqlanmoqda...' : <><Save className="w-4 h-4 inline" /> Saqlash</>}
                        </button>
                    </div>
                </div>
            )}

            {/* Tab: Content (Modules & Lessons) */}
            {tab === 'content' && course && (
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                    {/* Left: Module/Lesson tree */}
                    <div className="lg:col-span-2 space-y-4">
                        {course.modules?.map((mod, mi) => (
                            <div key={mod.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                {/* Module header */}
                                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
                                    <h3 className="font-semibold text-gray-900 text-sm">
                                        <span className="text-gray-400 mr-1">{mi + 1}.</span> {mod.title}
                                    </h3>
                                    <div className="flex gap-1">
                                        <button onClick={() => handleEditModule(mod.id)} className="p-1.5 hover:bg-gray-200 rounded-lg text-xs" title="Tahrirlash"><Pencil className="w-3.5 h-3.5 text-gray-500" /></button>
                                        <button onClick={() => handleDeleteModule(mod.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-xs text-red-500" title="O'chirish"><Trash2 className="w-3.5 h-3.5" /></button>
                                    </div>
                                </div>

                                {/* Lessons — Drag & Drop */}
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, mod.id)}>
                                    <SortableContext items={mod.lessons?.map(l => l.id) || []} strategy={verticalListSortingStrategy}>
                                        <div>
                                            {mod.lessons?.map((lesson, li) => (
                                                <SortableLessonItem
                                                    key={lesson.id}
                                                    lesson={lesson}
                                                    isActive={activeLesson?.id === lesson.id}
                                                    moduleIndex={mi}
                                                    lessonIndex={li}
                                                    onClick={() => setActiveLesson(lesson)}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>

                                {/* Add lesson button */}
                                <button
                                    onClick={() => handleAddLesson(mod.id)}
                                    className="w-full px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50 font-medium transition border-t border-gray-100"
                                >
                                    + Dars qo'shish
                                </button>
                            </div>
                        ))}

                        {/* Add module */}
                        <button
                            onClick={handleAddModule}
                            className="w-full py-4 border-2 border-dashed border-gray-200 rounded-2xl text-gray-500 hover:border-blue-500 hover:text-blue-600 font-medium text-sm transition"
                        >
                            + Yangi modul qo'shish
                        </button>
                    </div>

                    {/* Right: Lesson editor */}
                    <div className="lg:col-span-3">
                        {activeLesson ? (
                            <LessonEditor
                                key={activeLesson.id}
                                lesson={activeLesson}
                                onSave={handleSaveLesson}
                                onDelete={handleDeleteLesson}
                                onClose={() => setActiveLesson(null)}
                            />
                        ) : (
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center justify-center py-20 text-center">
                                <BookOpen className="w-12 h-12 text-gray-300 mb-4" />
                                <h3 className="text-lg font-bold text-gray-900 mb-2">Darsni tanlang yoki yarating</h3>
                                <p className="text-sm text-gray-500 max-w-sm">
                                    Chapdan modul qo'shing, keyin unga darslar qo'shing. Har bir darsga video yuklash va matn yozish mumkin.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>

        {/* Module name prompt dialog (add/edit) */}
        <PromptDialog
            open={modulePrompt.open}
            onClose={() => setModulePrompt({ open: false, moduleId: null, defaultValue: '' })}
            onSubmit={handleModulePromptSubmit}
            title={modulePrompt.moduleId ? "Modul nomini tahrirlash" : "Yangi modul qo'shish"}
            description={modulePrompt.moduleId ? "Modul uchun yangi nom kiriting" : "Yangi modul uchun nom kiriting"}
            label="Modul nomi"
            placeholder="Masalan: Asosiy tushunchalar"
            defaultValue={modulePrompt.defaultValue}
            icon={<Package className="w-5 h-5 text-blue-600" />}
        />

        {/* Lesson name prompt dialog */}
        <PromptDialog
            open={lessonPrompt.open}
            onClose={() => setLessonPrompt({ open: false, moduleId: null })}
            onSubmit={handleLessonPromptSubmit}
            title="Yangi dars qo'shish"
            description="Dars uchun nom kiriting"
            label="Dars nomi"
            placeholder="Masalan: Kirish darsi"
            icon={<FileText className="w-5 h-5 text-blue-600" />}
        />

        {/* Generic confirm dialog */}
        <ConfirmDialog
            open={confirmDialog.open}
            onClose={() => setConfirmDialog({ open: false, title: '', message: '', icon: '', onConfirm: null, danger: false })}
            onConfirm={confirmDialog.onConfirm || (() => {})}
            title={confirmDialog.title}
            message={confirmDialog.message}
            icon={confirmDialog.icon}
            danger={confirmDialog.danger}
        />
    </>
    );
}
